import type moment from 'moment'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import * as Analytics from 'analytics/analytics'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import ExploreQueryContext from 'tripPlanner/contexts/Explore/ExploreQueryContext'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import { useCreateTripItem } from 'tripPlanner/hooks/api/tripItem'
import { useTrip } from 'tripPlanner/hooks/api/trip'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import type { ExploreItem } from 'tripPlanner/types/common'
import { getMatchingSavedExploreItem } from 'tripPlanner/utils/explore'
import { createExploreItemBookmarkPayload } from 'tripPlanner/utils/itemConversions/bookmarks/exploreItem'
import BaseBookmarkToItinerary from '../../Common/BaseBookmarkToItinerary'

export interface Props {
  exploreItem: ExploreItem
  tripId?: string
  variant: 'chip' | 'button'
  dropdownPlacement?: 'top' | 'bottom'
}

function ExploreBookmarkToItineraryButton({
  exploreItem,
  tripId: tripIdProp,
  variant,
  dropdownPlacement,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const { canEdit } = useTripPermissions(tripId)
  const intl = useIntl()
  const addedToItineraryMessage = intl.formatMessage({
    id: 'gytvzB6a8/',
    defaultMessage: 'Added to itinerary',
    description: 'Success snackbar message when an explore item is added to the trip itinerary',
  })

  const savedItem = trip ?
      getMatchingSavedExploreItem(trip, exploreItem) :
    undefined

  const { showSaveErrorSnackbar } = useBookmarkSnackbarHandlers()

  const { category } = useContext(ExploreQueryContext)

  const { mutateAsync: createItem, isLoading: isCreating } = useCreateTripItem({
    onError: () => {
      showSaveErrorSnackbar(trip!.name)

      // Clear the recently saved trip ID in case it errored because the trip was deleted
      clearRecentlySavedTripId()
    },
  })

  const createTripItemIntoItinerary = useCallback(
    async(date: moment.Moment) => {
      if (!trip) {
        return
      }
      try {
        const isRestaurantOrCafe =
          category.label === 'Restaurants' || category.label === 'Cafes'
        const type = isRestaurantOrCafe ? 'RESTAURANT_BAR' : 'ATTRACTION'

        const payload = createExploreItemBookmarkPayload({
          exploreItem,
          startDate: date.format(ISO_DATE_FORMAT),
          type,
        })
        await createItem({
          tripId: trip.id,
          tripItem: payload,
        })
        showSnackbar(addedToItineraryMessage, 'success')
      } catch {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [trip, category.label, exploreItem, createItem, addedToItineraryMessage],
  )

  const onSelectDate = useCallback(
    () => {
      Analytics.trackClientEvent({
        action: 'click',
        subject: 'add_to_itinerary_explore_item',
        category: AnalyticsPage.tripPlanner,
        type: 'interaction',
      })
    },
    [],
  )

  if (!canEdit || !tripId) {
    return null
  }

  return <BaseBookmarkToItinerary
    savedItem={savedItem}
    onRequestCreateItem={createTripItemIntoItinerary}
    isCreatingItem={isCreating}
    tripId={tripId}
    variant={variant}
    datesRequiredMessageVariation="exploreItem"
    dropdownPlacement={dropdownPlacement}
    onSelectDate={onSelectDate}
  />
}

export default ExploreBookmarkToItineraryButton
